/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, Share2, MessageCircle, Star, MapPin, ChevronRight, Share, HeartOff } from 'lucide-react';
import { useTheme } from '../theme';
import { ROUTES } from '../routes';
import { ItemCard } from '../components/ItemCard';
import { itemService } from '../services/itemService';

interface Item {
  id: number;
  title: string;
  description: string;
  price: number;
  categoryName?: string;
  userId: number;
  storeId?: number;
  userName?: string;
  userRating?: number;
  userReviewCount?: number;
  status: string;
  location?: string;
  images?: string[];
}

export default function ItemDetail() {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [isSaved, setIsSaved] = useState(false);

  // Mock data for missing fields from API
  const mockImages = [
    'https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&q=80&w=800&h=600',
    'https://images.unsplash.com/photo-1581235720704-06d3acfcb36f?auto=format&fit=crop&q=80&w=800&h=600',
    'https://images.unsplash.com/photo-1558350315-8aa00e8e4590?auto=format&fit=crop&q=80&w=800&h=600',
    'https://images.unsplash.com/photo-1526733158272-60b4944e8741?auto=format&fit=crop&q=80&w=800&h=600',
  ];

  const relatedItems = [
    { id: 10, title: 'Hammer', price: 15, distance: '2 mi', rating: 4.2, image: 'https://images.unsplash.com/photo-1586864387917-f729a5578fd0?auto=format&fit=crop&q=80&w=200&h=200' },
    { id: 11, title: 'Wrench Set', price: 45, distance: '5 mi', rating: 4.7, image: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&q=80&w=200&h=200' },
    { id: 12, title: 'Toolbox', price: 80, distance: '4 mi', rating: 4.5, image: 'https://images.unsplash.com/photo-1581244277943-fe4a9c777189?auto=format&fit=crop&q=80&w=200&h=200' },
    { id: 13, title: 'Safety Gear', price: 25, distance: '3 mi', rating: 4.8, image: 'https://images.unsplash.com/photo-159742324403d-c1a5097a20c7?auto=format&fit=crop&q=80&w=200&h=200' },
  ];

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await itemService.getItemDetails(id!);
        const data = response.data.data;
        // Enriching data with mock fields if they are missing
        setItem({
          ...data,
          image: data.imageUrl,
          images: data.imageUrl ? [data.imageUrl] : mockImages,
          location: data.location || 'Springfield, IL',
          userName: data.userName || 'John Smith',
          userRating: data.userRating || 4.8,
          userReviewCount: data.userReviewCount || 120,
        });
      } catch (err) {
        setError('Item not found or an error occurred.');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;
  if (error || !item) return <div className="text-center mt-20 text-red-600 font-bold text-xl">{error || 'Item not found'}</div>;

  return (
    <div className="pb-20 md:pb-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Left: Image Gallery */}
        <div className="space-y-4">
          <div className="relative group aspect-[4/3] rounded-2xl overflow-hidden shadow-lg border" style={{ borderColor: theme.colors.border }}>
            <img
              src={item.images![selectedImage]}
              alt={item.title}
              className="w-full h-full object-cover cursor-zoom-in transition-transform duration-500 hover:scale-110"
              onClick={() => window.open(item.images![selectedImage], '_blank')}
            />
            {item.status !== 'Active' && (
              <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                {item.status}
              </div>
            )}
          </div>
          
          <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
            {item.images!.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedImage(idx)}
                className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 transition-all ${selectedImage === idx ? 'border-blue-500 scale-95 shadow-inner' : 'border-transparent opacity-70 hover:opacity-100 hover:scale-105'}`}
              >
                <img src={img} alt={`${item.title} ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Right: Item Info & Seller Panel */}
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2" style={{ color: theme.colors.text }}>
              {item.title}
            </h1>
            <div className="flex items-center gap-4 mb-6">
              <span className="text-4xl font-black" style={{ color: theme.colors.primary }}>
                ${Number(item.price).toLocaleString()}
              </span>
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-100" style={{ color: theme.colors.primary }}>
                <MapPin size={16} />
                <span className="text-sm font-bold">{item.location}</span>
              </div>
            </div>

            {/* Seller Panel */}
            <div className="p-6 rounded-2xl border bg-white shadow-sm flex items-center justify-between" style={{ borderColor: theme.colors.border }}>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center font-bold text-xl text-gray-500">
                  {item.userName![0]}
                </div>
                <div>
                  <h3 className="font-bold text-lg" style={{ color: theme.colors.text }}>{item.userName}</h3>
                  <div className="flex items-center gap-1.5">
                    <Star size={16} fill={theme.colors.accent} style={{ color: theme.colors.accent }} />
                    <span className="font-bold">{item.userRating}</span>
                    <span className="text-sm opacity-60">({item.userReviewCount} reviews)</span>
                  </div>
                </div>
              </div>
              <Link to={`/store/${item.userId}`} className="flex items-center gap-1 font-bold text-sm hover:opacity-80 transition-opacity" style={{ color: theme.colors.primary }}>
                View Storefront <ChevronRight size={18} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button className="flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:scale-[1.02] active:scale-95" style={{ backgroundColor: theme.colors.primary }}>
              <MessageCircle size={20} />
              Message Seller
            </button>
            <div className="flex gap-2">
              <button 
                onClick={() => setIsSaved(!isSaved)}
                className="flex-grow flex items-center justify-center gap-2 py-4 rounded-xl font-bold border transition-all hover:bg-gray-50 active:scale-95" 
                style={{ borderColor: theme.colors.border, color: isSaved ? theme.colors.danger : theme.colors.text }}
              >
                {isSaved ? <HeartOff size={20} /> : <Heart size={20} />}
                {isSaved ? 'Saved' : 'Save'}
              </button>
              <button className="p-4 rounded-xl font-bold border transition-all hover:bg-gray-50 active:scale-95" style={{ borderColor: theme.colors.border, color: theme.colors.text }}>
                <Share size={20} />
              </button>
            </div>
          </div>

          {/* Map Placeholder */}
          <div className="rounded-2xl overflow-hidden border aspect-[16/6] bg-gray-100 relative group" style={{ borderColor: theme.colors.border }}>
             <img 
               src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800&h=400" 
               className="w-full h-full object-cover opacity-60" 
               alt="Map placeholder" 
             />
             <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-xl flex items-center gap-2">
                  <MapPin size={20} className="text-blue-500" />
                  <span className="font-bold text-gray-800">{item.location}</span>
                </div>
             </div>
          </div>
        </div>
      </div>

      {/* Description Section */}
      <div className="mt-12 pt-12 border-t" style={{ borderColor: theme.colors.border }}>
        <h2 className="text-2xl font-bold mb-6" style={{ color: theme.colors.text }}>Description</h2>
        <div className="bg-white p-8 rounded-2xl border shadow-sm prose prose-blue max-w-none" style={{ borderColor: theme.colors.border }}>
          <p className="text-lg leading-relaxed text-gray-700 whitespace-pre-line">
            {item.description}
          </p>
        </div>
      </div>

      {/* Related Items Section */}
      <div className="mt-16 pt-12 border-t" style={{ borderColor: theme.colors.border }}>
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold" style={{ color: theme.colors.text }}>You may also like</h2>
          <Link to={ROUTES.SEARCH} className="flex items-center gap-1 font-bold text-sm hover:opacity-80 transition-opacity" style={{ color: theme.colors.primary }}>
             See More <ChevronRight size={18} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {relatedItems.map((relItem) => (
            <ItemCard key={relItem.id} item={relItem} />
          ))}
        </div>
      </div>
    </div>
  );
}
